import {
  BEDTIME,
  DAY_LENGTH,
  GOLDEN_XP_MULTIPLIER,
  HYBRID_BONUS_SEED_CHANCE,
  MAX_FARM_NAME_LENGTH,
  PLOT_BASE_COST,
  PLOT_COST_GROWTH,
  SEED_DROP_CHANCE,
  STARTING_PLOTS,
  WAKE_TIME,
} from './constants';
import type { GameEvent } from './events';
import { FLOWERS, findHybrid, isFlowerId, type FlowerId } from './flowers';
import { isValidPlotId, neighbors, plotCell, plotIdAt } from './grid';
import { sellPrice } from './market';
import { canFulfill } from './orders';
import { levelFromXp } from './progression';
import { Rng } from './rng';
import { advance } from './simulation';
import { addSeeds, seedCount, type GameState } from './state';
import {
  crossPollinationChance,
  isUpgradeId,
  maxLevel,
  upgradeCost,
  UPGRADES,
  wateringPattern,
  type UpgradeId,
} from './upgrades';
import { stripControlChars } from '../utils/text';

export type GameAction =
  | { type: 'plant'; plotId: number; species: FlowerId }
  | { type: 'water'; plotId: number }
  | { type: 'harvest'; plotId: number }
  | { type: 'dig'; plotId: number }
  | { type: 'unlockPlot'; plotId: number }
  | { type: 'buySeeds'; species: FlowerId; qty: number }
  | { type: 'sell'; species: FlowerId; golden: boolean; qty: number }
  | { type: 'buyUpgrade'; upgrade: UpgradeId }
  | { type: 'deliverOrder'; orderId: number }
  | { type: 'discardOrder'; orderId: number }
  | { type: 'sleep' }
  | { type: 'renameFarm'; name: string };

export type ActionResult = { ok: true; events: GameEvent[] } | { ok: false; reason: string };

/** Largest quantity a single buy/sell action may move (also enforced on the wire). */
export const MAX_TRADE_QTY = 999;

const fail = (reason: string): ActionResult => ({ ok: false, reason });
const ok = (...events: GameEvent[]): ActionResult => ({ ok: true, events });

export function plotUnlockCost(state: GameState): number {
  const unlocked = state.plots.filter((p) => p.unlocked).length;
  const extra = Math.max(0, unlocked - STARTING_PLOTS.length);
  return Math.round(PLOT_BASE_COST * Math.pow(PLOT_COST_GROWTH, extra));
}

export function canSleep(dayTime: number): boolean {
  return dayTime >= BEDTIME || dayTime < WAKE_TIME - 0.02;
}

export function sanitizeFarmName(name: string): string {
  return stripControlChars(name).trim().slice(0, MAX_FARM_NAME_LENGTH);
}

function grantXp(state: GameState, amount: number, events: GameEvent[]): void {
  const before = levelFromXp(state.xp);
  state.xp += amount;
  const after = levelFromXp(state.xp);
  for (let level = before + 1; level <= after; level++) events.push({ type: 'levelUp', level });
}

/** Removes `qty` flowers of a species, spending normal ones before golden ones. */
function consumeFlowers(state: GameState, species: FlowerId, qty: number): void {
  const stock = state.flowers[species];
  if (!stock) return;
  const fromNormal = Math.min(stock.n, qty);
  stock.n -= fromNormal;
  stock.g -= qty - fromNormal;
}

function harvest(state: GameState, plotId: number): ActionResult {
  const plot = state.plots[plotId];
  const crop = plot?.crop;
  if (!plot || !crop) return fail('Nothing to harvest here.');
  if (crop.growth < 1) return fail(`${FLOWERS[crop.species].name} isn't ready yet.`);

  const rng = new Rng(state);
  const def = FLOWERS[crop.species];
  const events: GameEvent[] = [];

  const stock = (state.flowers[crop.species] ??= { n: 0, g: 0 });
  if (crop.golden) stock.g++;
  else stock.n++;

  // Bred flowers always give a seed back so a lucky cross is never lost.
  let seedsDropped = 0;
  if (def.seedCost === null) seedsDropped = 1 + (rng.chance(HYBRID_BONUS_SEED_CHANCE) ? 1 : 0);
  else if (rng.chance(SEED_DROP_CHANCE)) seedsDropped = 1;
  if (seedsDropped > 0) addSeeds(state, crop.species, seedsDropped);

  // Cross-pollination with a blooming neighbour of a compatible species.
  let hybrid: FlowerId | null = null;
  const crossChance = crossPollinationChance(state.upgrades.beehive);
  for (const neighborId of neighbors(plotId)) {
    const other = state.plots[neighborId]?.crop;
    if (!other || other.growth < 1) continue;
    const candidate = findHybrid(crop.species, other.species);
    if (candidate && rng.chance(crossChance)) {
      hybrid = candidate;
      addSeeds(state, candidate, 1);
      break;
    }
  }

  const entry = (state.almanac[crop.species] ??= { harvested: 0, golden: 0 });
  const firstHarvest = entry.harvested === 0;
  entry.harvested++;
  if (crop.golden) entry.golden++;
  state.stats.harvested++;

  events.push({
    type: 'harvested',
    plotId,
    species: crop.species,
    golden: crop.golden,
    seedsDropped,
    hybrid,
  });
  if (firstHarvest) events.push({ type: 'discovered', species: crop.species });
  grantXp(state, def.xp * (crop.golden ? GOLDEN_XP_MULTIPLIER : 1), events);

  plot.crop = null;
  return { ok: true, events };
}

function sleep(state: GameState): ActionResult {
  if (!canSleep(state.dayTime)) return fail("It's too early for bed — come back after sunset.");
  const fraction = (1 + WAKE_TIME - state.dayTime) % 1;
  const seconds = fraction * DAY_LENGTH;
  const happened = advance(state, seconds, { authoritative: true });
  // Snap exactly to morning to avoid float drift across many nights.
  state.dayTime = WAKE_TIME;
  const bloomed = happened.filter((e) => e.type === 'bloomed').length;
  const keep = happened.filter((e) => e.type === 'newDay' || e.type === 'newOrder');
  return ok({ type: 'slept', seconds, bloomed }, ...keep);
}

/**
 * Validates and applies a player action to the state (mutating it in place).
 * All rule enforcement lives here so solo play and multiplayer hosts share it.
 */
export function applyAction(state: GameState, action: GameAction): ActionResult {
  switch (action.type) {
    case 'plant': {
      const plot = state.plots[action.plotId];
      if (!plot || !plot.unlocked) return fail('You need to buy this plot first.');
      if (plot.crop) return fail('Something is already growing here.');
      if (!isFlowerId(action.species)) return fail('Unknown seed.');
      if (seedCount(state, action.species) <= 0) {
        return fail(`No ${FLOWERS[action.species].name} seeds left.`);
      }
      state.seeds[action.species] = seedCount(state, action.species) - 1;
      plot.crop = { species: action.species, growth: 0, golden: false };
      state.stats.planted++;
      return ok({ type: 'planted', plotId: plot.id, species: action.species });
    }

    case 'water': {
      const origin = state.plots[action.plotId];
      if (!origin || !origin.unlocked) return fail('You can only water your own plots.');
      const { col, row } = plotCell(origin.id);
      const plotIds: number[] = [];
      for (const [dc, dr] of wateringPattern(state.upgrades.wateringCan)) {
        const id = plotIdAt(col + dc, row + dr);
        const plot = id === null ? undefined : state.plots[id];
        if (plot?.unlocked) {
          plot.moisture = 1;
          plotIds.push(plot.id);
        }
      }
      return ok({ type: 'watered', plotIds });
    }

    case 'harvest':
      return harvest(state, action.plotId);

    case 'dig': {
      const plot = state.plots[action.plotId];
      if (!plot?.crop) return fail('Nothing to dig up.');
      plot.crop = null;
      return ok({ type: 'dug', plotId: plot.id });
    }

    case 'unlockPlot': {
      if (!isValidPlotId(action.plotId)) return fail('Unknown plot.');
      const plot = state.plots[action.plotId];
      if (!plot || plot.unlocked) return fail('You already own this plot.');
      const cost = plotUnlockCost(state);
      if (state.coins < cost) return fail(`You need 🪙${cost} to buy this plot.`);
      state.coins -= cost;
      plot.unlocked = true;
      plot.moisture = 0;
      return ok({ type: 'plotUnlocked', plotId: plot.id, cost });
    }

    case 'buySeeds': {
      if (!isFlowerId(action.species)) return fail('Unknown seed.');
      const def = FLOWERS[action.species];
      const qty = Math.floor(action.qty);
      if (!(qty >= 1 && qty <= MAX_TRADE_QTY)) return fail('Invalid quantity.');
      if (def.seedCost === null) return fail(`${def.name} seeds can't be bought — breed them!`);
      if (levelFromXp(state.xp) < def.unlockLevel) {
        return fail(`${def.name} unlocks at level ${def.unlockLevel}.`);
      }
      const cost = def.seedCost * qty;
      if (state.coins < cost) return fail(`You need 🪙${cost} for that.`);
      state.coins -= cost;
      addSeeds(state, action.species, qty);
      return ok({ type: 'seedsBought', species: action.species, qty, cost });
    }

    case 'sell': {
      if (!isFlowerId(action.species)) return fail('Unknown flower.');
      const qty = Math.floor(action.qty);
      if (!(qty >= 1 && qty <= MAX_TRADE_QTY)) return fail('Invalid quantity.');
      const stock = state.flowers[action.species];
      const have = stock ? (action.golden ? stock.g : stock.n) : 0;
      if (!stock || have < qty) return fail("You don't have that many to sell.");
      const earned = sellPrice(state, action.species, action.golden) * qty;
      if (action.golden) stock.g -= qty;
      else stock.n -= qty;
      state.coins += earned;
      state.stats.coinsEarned += earned;
      return ok({ type: 'sold', species: action.species, golden: action.golden, qty, earned });
    }

    case 'buyUpgrade': {
      if (!isUpgradeId(action.upgrade)) return fail('Unknown upgrade.');
      const current = state.upgrades[action.upgrade];
      if (current >= maxLevel(action.upgrade)) return fail('Already fully upgraded!');
      const def = UPGRADES[action.upgrade];
      if (levelFromXp(state.xp) < def.unlockLevel) {
        return fail(`${def.name} unlocks at level ${def.unlockLevel}.`);
      }
      const cost = upgradeCost(action.upgrade, current);
      if (cost === null || state.coins < cost) return fail(`You need 🪙${cost ?? '?'} for that.`);
      state.coins -= cost;
      state.upgrades[action.upgrade] = current + 1;
      if (action.upgrade === 'sprinkler') state.sprinklerTimer = 0;
      return ok({ type: 'upgradeBought', upgrade: action.upgrade, level: current + 1 });
    }

    case 'deliverOrder': {
      const index = state.orders.findIndex((o) => o.id === action.orderId);
      const order = state.orders[index];
      if (!order) return fail('That order is gone.');
      if (!canFulfill(state, order)) return fail('You are missing some flowers for this order.');
      for (const item of order.items) consumeFlowers(state, item.species, item.qty);
      state.orders.splice(index, 1);
      state.coins += order.reward;
      state.stats.coinsEarned += order.reward;
      state.stats.ordersCompleted++;
      const events: GameEvent[] = [
        {
          type: 'orderDelivered',
          orderId: order.id,
          customer: order.customer,
          reward: order.reward,
          xp: order.xp,
        },
      ];
      grantXp(state, order.xp, events);
      return { ok: true, events };
    }

    case 'discardOrder': {
      const index = state.orders.findIndex((o) => o.id === action.orderId);
      if (index < 0) return fail('That order is gone.');
      state.orders.splice(index, 1);
      // A fresh customer shows up soon after one is turned away.
      state.orderTimer = Math.min(state.orderTimer, 20);
      return ok({ type: 'orderDiscarded', orderId: action.orderId });
    }

    case 'sleep':
      return sleep(state);

    case 'renameFarm': {
      const name = sanitizeFarmName(action.name);
      if (!name) return fail('Your farm needs a name!');
      state.farmName = name;
      return ok({ type: 'farmRenamed', name });
    }

    default: {
      const exhaustive: never = action;
      return fail(`Unknown action ${(exhaustive as { type?: string }).type ?? ''}`);
    }
  }
}
